if (!customElements.get('pj-recommendations')) {
  customElements.define(
    'pj-recommendations',
    class PjRecommendations extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick.bind(this));
        this.trackCurrentProduct();
        if (this.dataset.source === 'recent') this.loadRecentlyViewed();
      }

      recentHandles() {
        try {
          const storedHandles = JSON.parse(localStorage.getItem('pj_recent') || '[]');
          return Array.isArray(storedHandles) ? [...new Set(storedHandles.filter(Boolean))] : [];
        } catch {
          return [];
        }
      }

      trackCurrentProduct() {
        const currentHandle = this.dataset.currentHandle;
        if (!currentHandle) return;
        const handles = this.recentHandles().filter((handle) => handle !== currentHandle);
        try {
          localStorage.setItem('pj_recent', JSON.stringify([currentHandle, ...handles].slice(0, 12)));
        } catch {
          // Browsing can continue when storage is unavailable; the recent source stays hidden.
        }
      }

      async loadRecentlyViewed() {
        const currentHandle = this.dataset.currentHandle;
        const limit = Number(this.dataset.productsLimit) || 4;
        const handles = this.recentHandles()
          .filter((handle) => handle !== currentHandle)
          .slice(0, limit);
        if (!handles.length) return;

        const cards = await Promise.all(
          handles.map((handle) => this.fetchRecentlyViewedCard(handle, this.dataset.sectionId))
        );
        const validCards = cards.filter(Boolean);
        if (!validCards.length) return;

        this.querySelector('[data-pj-rec-grid]').innerHTML = validCards
          .map((card) => `<li>${card}</li>`)
          .join('');
        this.hidden = false;
      }

      async fetchRecentlyViewedCard(handle, sectionId) {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(
            `${root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`
          );
          if (!response.ok) return '';
          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const template = documentFragment.querySelector('template[data-pj-rec-current-card]');
          return template?.content.querySelector('[data-pj-rec-card]')?.outerHTML || '';
        } catch {
          return '';
        }
      }

      onClick(event) {
        const action = event.target.closest(
          '[data-pj-rec-open], [data-pj-rec-close], [data-pj-rec-prev], [data-pj-rec-next], ' +
            '[data-pj-rec-option-value], [data-pj-rec-submit], [data-pj-rec-direct-add]'
        );
        if (!action) return;

        const card = action.closest('[data-pj-rec-card]');
        const dialog = card?.querySelector('[data-pj-rec-dialog]');

        if (action.matches('[data-pj-rec-open]')) this.openDialog(dialog);
        if (action.matches('[data-pj-rec-close]')) this.closeDialog(dialog);
        if (action.matches('[data-pj-rec-prev]')) this.moveGallery(dialog, -1);
        if (action.matches('[data-pj-rec-next]')) this.moveGallery(dialog, 1);
        if (action.matches('[data-pj-rec-option-value]')) this.selectOption(dialog, action);
        if (action.matches('[data-pj-rec-submit]')) this.addSelectedVariant(dialog, action);
        if (action.matches('[data-pj-rec-direct-add]')) this.addVariant(action.dataset.variantId, action);
      }

      openDialog(dialog) {
        if (!dialog) return;
        this.prepareDialog(dialog);
        dialog.showModal();
        document.body.classList.add('overflow-hidden');
      }

      closeDialog(dialog) {
        if (!dialog) return;
        dialog.close();
        document.body.classList.remove('overflow-hidden');
      }

      prepareDialog(dialog) {
        if (dialog.dataset.ready === 'true') return;
        dialog.variants = JSON.parse(dialog.querySelector('[data-pj-rec-variants]').textContent);
        dialog.media = JSON.parse(dialog.querySelector('[data-pj-rec-media]').textContent);
        dialog.galleryIndex = 0;
        const image = dialog.querySelector('[data-pj-rec-gallery-image]');
        image.src = image.dataset.initialSrc;
        image.removeAttribute('data-initial-src');
        dialog.dataset.ready = 'true';
        dialog.addEventListener('close', () => document.body.classList.remove('overflow-hidden'));
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) this.closeDialog(dialog);
        });
      }

      moveGallery(dialog, direction) {
        if (!dialog?.media?.length) return;
        dialog.galleryIndex = (dialog.galleryIndex + direction + dialog.media.length) % dialog.media.length;
        this.showImage(dialog, dialog.media[dialog.galleryIndex].src, dialog.media[dialog.galleryIndex].alt);
      }

      showImage(dialog, src, alt) {
        const image = dialog.querySelector('[data-pj-rec-gallery-image]');
        image.src = src;
        image.alt = alt || '';
        const mediaIndex = dialog.media?.findIndex((media) => media.src === src);
        if (mediaIndex >= 0) dialog.galleryIndex = mediaIndex;
      }

      selectOption(dialog, button) {
        const option = button.closest('[data-pj-rec-option]');
        option.querySelectorAll('[data-pj-rec-option-value]').forEach((value) => {
          const isSelected = value === button;
          value.classList.toggle('is-selected', isSelected);
          value.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        const variant = this.selectedVariant(dialog);
        const colorVariant = option.hasAttribute('data-color-option')
          ? dialog.variants.find(
              (candidate) => candidate.available && candidate.options[Number(option.dataset.optionIndex)] === button.dataset.pjRecOptionValue
            )
          : null;
        const imageVariant = variant || colorVariant;
        if (imageVariant?.image) this.showImage(dialog, imageVariant.image, '');
        this.updateVariantState(dialog, variant);
      }

      selectedOptions(dialog) {
        return Array.from(dialog.querySelectorAll('[data-pj-rec-option]')).map(
          (option) => option.querySelector('.is-selected')?.dataset.pjRecOptionValue
        );
      }

      selectedVariant(dialog) {
        const selectedOptions = this.selectedOptions(dialog);
        return dialog.variants.find((variant) =>
          variant.options.every((option, index) => option === selectedOptions[index])
        );
      }

      updateVariantState(dialog, variant) {
        const submit = dialog.querySelector('[data-pj-rec-submit]');
        const price = dialog.querySelector('[data-pj-rec-price]');
        const error = dialog.querySelector('[data-pj-rec-error]');
        const available = Boolean(variant?.available);

        submit.disabled = !available;
        submit.dataset.variantId = variant?.id || '';
        submit.querySelector('span').textContent = available
          ? `${this.dataset.addLabel} \u00b7 ${variant.price_formatted}`
          : this.dataset.unavailableLabel;
        if (variant) price.textContent = variant.price_formatted;
        error.hidden = true;
      }

      addSelectedVariant(dialog, button) {
        const variant = this.selectedVariant(dialog);
        if (!variant?.available) return;
        this.addVariant(variant.id, button, dialog);
      }

      async addVariant(variantId, button, dialog = null) {
        const spinner = button.querySelector('.loading__spinner');
        const error = dialog?.querySelector('[data-pj-rec-error]');
        button.disabled = true;
        button.classList.add('is-loading');
        spinner?.classList.remove('hidden');
        if (error) error.hidden = true;

        try {
          const drawer = document.querySelector('cart-drawer');
          const sections = drawer
            ? drawer.getSectionsToRender().map((section) => section.id)
            : ['cart-icon-bubble'];
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);

          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            body: formData,
          });
          const cartState = await response.json();
          if (!response.ok || cartState.status) throw new Error(cartState.description || cartState.message);

          if (dialog) this.closeDialog(dialog);
          if (drawer && cartState.sections) {
            drawer.setActiveElement(button);
            drawer.renderContents(cartState);
          } else {
            window.location.assign(window.routes.cart_url);
          }
        } catch (errorMessage) {
          if (error) {
            error.textContent = errorMessage.message || this.dataset.errorLabel;
            error.hidden = false;
          }
        } finally {
          button.disabled = false;
          button.classList.remove('is-loading');
          spinner?.classList.add('hidden');
        }
      }
    }
  );
}
